import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Database, Network, Plus, RefreshCw, Search } from "lucide-react";
import { useState } from "react";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  LoadingState,
  NotConnectedState,
} from "@/components/states";
import { PageHeader } from "@/components/bespoke";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MemoryGraphCanvas } from "@/components/memory-graph/memory-graph-canvas";
import {
  ApiError,
  addMemoryFact,
  createMemorySource,
  memoryFactsQuery,
  memorySourcesQuery,
  recallMemory,
  recallServiceMemory,
  reindexMemory,
  updateMemoryFact,
  type MemoryEntry,
} from "@/lib/api";
import { useCurrentProject } from "@/lib/project";
import { formatCredits, formatMemoryDate, formatZonedDateTime } from "@/lib/format";

const stateLabels = {
  pending: "Pending",
  indexing: "Indexing",
  ready: "Indexed",
  failed: "Failed",
} as const;
const stateVariants = {
  pending: "unknown",
  indexing: "info",
  ready: "success",
  failed: "danger",
} as const;
const kindLabels = { repository: "Repository", documents: "Documents", manual: "Facts" } as const;

export function MemoryPage({ headingLevel = "h1" }: { headingLevel?: "h1" | "h2" } = {}) {
  const { project, loaded } = useCurrentProject();
  const queryClient = useQueryClient();
  const query = useQuery({
    ...memorySourcesQuery(project?.id ?? ""),
    enabled: loaded && project !== null,
  });
  const storedFacts = useQuery({
    ...memoryFactsQuery(project?.id ?? ""),
    enabled: loaded && project !== null,
  });
  const [phrase, setPhrase] = useState("");
  const [recallPhrase, setRecallPhrase] = useState("");
  const [recallResults, setRecallResults] = useState<Awaited<
    ReturnType<typeof recallMemory>
  > | null>(null);
  const [serviceResults, setServiceResults] = useState<Awaited<
    ReturnType<typeof recallServiceMemory>
  > | null>(null);
  const [facts, setFacts] = useState<MemoryEntry[]>([]);
  const [fact, setFact] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [sourceReference, setSourceReference] = useState("");
  const [sourceKind, setSourceKind] = useState<"repository" | "documents">("repository");
  const [feedback, setFeedback] = useState<string | null>(null);

  // Project switches must never leak the previous tenant's recall, facts, or drafts.
  const [projectScope, setProjectScope] = useState(project?.id ?? null);
  if (projectScope !== (project?.id ?? null)) {
    setProjectScope(project?.id ?? null);
    setPhrase("");
    setRecallPhrase("");
    setRecallResults(null);
    setServiceResults(null);
    setFacts([]);
    setFact("");
    setEditingId(null);
    setEditingBody("");
    setSourceName("");
    setSourceReference("");
    setSourceKind("repository");
    setFeedback(null);
  }

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["projects", project?.id, "memory"] });
  const sourceMutation = useMutation({
    mutationFn: () =>
      createMemorySource(project!.id, {
        name: sourceName,
        reference: sourceReference,
        kind: sourceKind,
      }),
    onSuccess: async () => {
      setSourceName("");
      setSourceReference("");
      setFeedback("Source added.");
      await refresh();
    },
    onError: () => setFeedback("The source could not be added. Check the details and try again."),
  });
  const reindexMutation = useMutation({
    mutationFn: () => reindexMemory(project!.id),
    onSuccess: async () => {
      setFeedback("Re-index complete.");
      await refresh();
    },
    onError: () => setFeedback("Re-indexing could not be completed. Try again."),
  });
  const recallMutation = useMutation({
    mutationFn: async () => {
      const [rows, service] = await Promise.all([
        recallMemory(project!.id, { phrase: recallPhrase, limit: 5 }),
        // The shared memory service augments the built-in store when the console has it switched on.
        recallServiceMemory(project!.id, { phrase: recallPhrase, limit: 5 }).catch(() => null),
      ]);
      setServiceResults(service);
      return rows;
    },
    onSuccess: (rows) => {
      setRecallResults(rows);
      setFacts(rows.filter((row) => row.sourceId === factsSource?.id));
      setFeedback(null);
    },
    onError: () => {
      setRecallResults(null);
      setFeedback("Recall could not be completed. Try again.");
    },
  });
  const factMutation = useMutation({
    mutationFn: () => addMemoryFact(project!.id, { body: fact }),
    onSuccess: async (added) => {
      setFact("");
      setFacts((current) => [...current, added]);
      setFeedback("Fact added.");
      await refresh();
    },
    onError: () => setFeedback("The fact could not be added. Try again."),
  });
  const editMutation = useMutation({
    mutationFn: () => updateMemoryFact(project!.id, editingId!, { body: editingBody }),
    onSuccess: async (updated) => {
      setEditingId(null);
      setFacts((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      setFeedback("Fact updated.");
      await refresh();
    },
    onError: () => setFeedback("The fact could not be updated. Try again."),
  });

  const sources = query.data ?? [];
  const factsSource = sources.find((source) => source.kind === "manual");
  const persistedFacts = storedFacts.data ?? [];
  const manualFacts = [...persistedFacts, ...facts].filter(
    (item, index, all) =>
      (!factsSource || item.sourceId === factsSource.id) &&
      all.findIndex((other) => other.id === item.id) === index,
  );

  if (!loaded || (project !== null && query.isPending)) {
    return (
      <>
        <PageHeading headingLevel={headingLevel} />
        <LoadingState label="Loading project memory" rows={5} />
      </>
    );
  }
  if (project === null)
    return (
      <>
        <PageHeading headingLevel={headingLevel} />
        <EmptyState title="No projects yet" description="Create a project to manage its memory." />
      </>
    );
  if (query.isError) {
    const error = query.error;
    return (
      <>
        <PageHeading headingLevel={headingLevel} />
        {error instanceof ApiError && error.status === 404 ? (
          <ForbiddenState
            title="You do not have access to this project"
            description="Ask an owner to invite you. Nothing from the project is shown."
          />
        ) : (
          <ErrorState
            title="Memory could not be loaded"
            description={
              error instanceof ApiError
                ? (error.problem?.detail ?? error.message)
                : "The request could not be completed."
            }
            requestId={error instanceof ApiError ? (error.requestId ?? "unknown") : "unknown"}
            onRetry={() => void query.refetch()}
          />
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeading headingLevel={headingLevel} />
      {feedback && (
        <p role="status" className="text-sm text-muted-foreground">
          {feedback}
        </p>
      )}

      <Tabs defaultValue="sources" className="w-full">
        <TabsList aria-label="Memory views" className="mb-4">
          <TabsTrigger value="sources" className="gap-1.5">
            <Database className="size-3.5" aria-hidden /> Sources & Facts
          </TabsTrigger>
          <TabsTrigger value="graph" className="gap-1.5">
            <Network className="size-3.5" aria-hidden /> Knowledge Graph
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Knowledge Graph View */}
        <TabsContent value="graph">
          {storedFacts.isPending ? (
            <LoadingState label="Loading knowledge graph" rows={3} />
          ) : storedFacts.isError ? (
            <ErrorState
              title="Knowledge graph could not be loaded"
              description={
                storedFacts.error instanceof ApiError
                  ? (storedFacts.error.problem?.detail ?? storedFacts.error.message)
                  : "The request could not be completed."
              }
              requestId={
                storedFacts.error instanceof ApiError
                  ? (storedFacts.error.requestId ?? "unknown")
                  : "unknown"
              }
              onRetry={() => void storedFacts.refetch()}
            />
          ) : (
            <MemoryGraphCanvas entries={manualFacts} projectName={project.name} />
          )}
        </TabsContent>

        {/* Tab 2: Classic Sources & Facts Grid */}
        <TabsContent value="sources">
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1.7fr)_minmax(18rem,1fr)]">
            <div className="flex flex-col gap-6">
              <Card>
                <div className="flex items-center justify-between border-b px-5 py-4">
                  <h2 className="font-semibold">Sources</h2>
                  <span className="text-sm text-muted-foreground">
                    {formatCredits(sources.reduce((sum, row) => sum + row.itemCount, 0))} items
                  </span>
                </div>
                {sources.length === 0 ? (
                  <div className="p-5">
                    <NotConnectedState
                      title="Memory sources are not connected yet"
                      description="Sources are recorded, but no indexer runs, so repository and document sources are not indexed. Manual facts below work today."
                    />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <caption className="sr-only">
                        Project memory sources and indexing status
                      </caption>
                      <thead>
                        <tr>
                          {["Source", "Kind", "Index state", "Items", "Last sync"].map((label) => (
                            <th
                              key={label}
                              scope="col"
                              className="h-10 border-b px-3 text-left text-xs font-medium text-muted-foreground first:pl-5 last:pr-5"
                            >
                              {label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sources.map((source) => (
                          <tr key={source.id} className="hover:bg-muted/50">
                            <td className="border-b px-3 py-3 first:pl-5 font-medium">
                              {source.name}
                            </td>
                            <td className="border-b px-3 py-3">{kindLabels[source.kind]}</td>
                            <td className="border-b px-3 py-3">
                              <Badge variant={stateVariants[source.state]}>
                                {stateLabels[source.state]}
                              </Badge>
                            </td>
                            <td className="border-b px-3 py-3 tabular-nums">
                              {formatCredits(source.itemCount)}
                            </td>
                            <td className="border-b px-3 py-3 last:pr-5 tabular-nums">
                              {formatMemoryDate(source.lastSyncAt)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <CardContent className="border-t">
                  <h3 className="text-sm font-medium">Add source</h3>
                  <form
                    className="mt-3 grid gap-3 sm:grid-cols-2"
                    onSubmit={(event) => {
                      event.preventDefault();
                      sourceMutation.mutate();
                    }}
                  >
                    <label className="grid gap-1 text-sm">
                      Source name
                      <Input
                        aria-label="Source name"
                        value={sourceName}
                        onChange={(event) => setSourceName(event.target.value)}
                        placeholder="Engineering handbook"
                      />
                    </label>
                    <label className="grid gap-1 text-sm">
                      Kind
                      <select
                        aria-label="Source kind"
                        className="h-11 md:h-9 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        value={sourceKind}
                        onChange={(event) =>
                          setSourceKind(event.target.value as "repository" | "documents")
                        }
                      >
                        <option value="repository">Repository</option>
                        <option value="documents">Documents</option>
                      </select>
                    </label>
                    <label className="grid gap-1 text-sm sm:col-span-2">
                      Reference
                      <Input
                        aria-label="Source reference"
                        value={sourceReference}
                        onChange={(event) => setSourceReference(event.target.value)}
                        placeholder="repo:team/project or a document reference"
                      />
                    </label>
                    <div>
                      <Button
                        type="submit"
                        disabled={
                          !sourceName.trim() || !sourceReference.trim() || sourceMutation.isPending
                        }
                      >
                        <Plus aria-hidden className="size-4" />
                        Add source
                      </Button>
                    </div>
                  </form>
                </CardContent>
                <div className="border-t px-5 py-3">
                  <Button
                    variant="outline"
                    onClick={() => reindexMutation.mutate()}
                    disabled={!sources.length || reindexMutation.isPending}
                  >
                    <RefreshCw aria-hidden className="size-4" />
                    {reindexMutation.isPending ? "Re-indexing…" : "Re-index all"}
                  </Button>
                </div>
              </Card>

              <Card>
                <div className="border-b px-5 py-4">
                  <h2 className="font-semibold">Try a recall</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Preview what a run would retrieve for a phrase.
                  </p>
                </div>
                <CardContent>
                  <form
                    className="flex flex-wrap gap-2"
                    onSubmit={(event) => {
                      event.preventDefault();
                      setRecallPhrase(phrase);
                      recallMutation.mutate();
                    }}
                  >
                    <Input
                      aria-label="Recall phrase"
                      className="min-w-0 flex-1 basis-48"
                      value={phrase}
                      onChange={(event) => setPhrase(event.target.value)}
                      placeholder="Search project memory"
                    />
                    <Button
                      variant="outline"
                      type="submit"
                      disabled={!phrase.trim() || recallMutation.isPending}
                    >
                      <Search aria-hidden className="size-4" />
                      {recallMutation.isPending ? "Searching…" : "Search"}
                    </Button>
                  </form>
                  {recallResults === null ? (
                    <p className="mt-4 text-sm text-muted-foreground">
                      Enter a phrase to preview relevant project memory.
                    </p>
                  ) : recallResults.length === 0 ? (
                    <div className="mt-4">
                      <EmptyState
                        title="No matching memories"
                        description="Try another phrase or add a manual fact."
                      />
                    </div>
                  ) : (
                    <ul className="mt-4 divide-y rounded-md border">
                      {recallResults.map((result) => {
                        const source = sources.find((item) => item.id === result.sourceId);
                        return (
                          <li
                            key={result.id}
                            className="flex items-start gap-3 p-4 hover:bg-muted/50"
                          >
                            <Badge variant="outline">
                              {source ? kindLabels[source.kind] : "Memory"}
                            </Badge>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm">{result.body}</p>
                              <p className="mt-1 text-xs text-muted-foreground">
                                {result.ref ?? "Project memory"} · used by {result.runsUsed}{" "}
                                {result.runsUsed === 1 ? "run" : "runs"}
                              </p>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {serviceResults && serviceResults.length > 0 ? (
                    <div className="mt-4">
                      <h3 className="text-sm font-medium">From the shared memory service</h3>
                      <ul className="mt-2 divide-y rounded-md border">
                        {serviceResults.map((memory) => (
                          <li key={memory.id} className="flex items-start gap-3 p-4">
                            <Badge variant="outline">
                              {memory.memoryType.toLowerCase() || "memory"}
                            </Badge>
                            <div className="min-w-0 flex-1">
                              <p className="whitespace-pre-wrap text-sm">
                                {memory.summary ?? memory.content}
                              </p>
                              {memory.createdAt ? (
                                <p className="mt-1 text-xs text-muted-foreground">
                                  {formatZonedDateTime(memory.createdAt, "UTC")}
                                </p>
                              ) : null}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            </div>

            <aside className="flex flex-col gap-6">
              <Card>
                <div className="border-b px-5 py-4">
                  <h2 className="font-semibold">Facts</h2>
                </div>
                <CardContent>
                  <form
                    className="grid gap-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      factMutation.mutate();
                    }}
                  >
                    <label className="grid gap-1 text-sm">
                      <span>Add a fact</span>
                      <Textarea
                        aria-label="Add a fact"
                        id="memory-fact"
                        rows={3}
                        value={fact}
                        onChange={(event) => setFact(event.target.value)}
                        placeholder="Something runs should always know about this project"
                      />
                    </label>
                    <div>
                      <Button type="submit" disabled={!fact.trim() || factMutation.isPending}>
                        <Plus aria-hidden className="size-4" />
                        Add fact
                      </Button>
                    </div>
                  </form>
                </CardContent>
                <ul className="divide-y border-t">
                  {manualFacts.map((item) => (
                    <li key={item.id} className="flex items-start gap-2 p-4">
                      {editingId === item.id ? (
                        <form
                          className="flex min-w-0 flex-1 flex-col gap-2"
                          onSubmit={(event) => {
                            event.preventDefault();
                            editMutation.mutate();
                          }}
                        >
                          <Textarea
                            aria-label="Edit fact"
                            value={editingBody}
                            onChange={(event) => setEditingBody(event.target.value)}
                          />
                          <div className="flex gap-2">
                            <Button
                              type="submit"
                              size="sm"
                              disabled={!editingBody.trim() || editMutation.isPending}
                            >
                              Save
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              type="button"
                              onClick={() => setEditingId(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </form>
                      ) : (
                        <>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm">{item.body}</p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              Updated {formatMemoryDate(item.updatedAt)}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingId(item.id);
                              setEditingBody(item.body);
                            }}
                          >
                            Edit
                          </Button>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
                {factsSource && (
                  <p className="border-t px-5 py-3 text-xs text-muted-foreground">
                    {formatCredits(factsSource.itemCount)} facts · status is always shown as a
                    labelled badge.
                  </p>
                )}
              </Card>
              <Card>
                <CardContent className="flex items-start gap-3 text-sm">
                  <Database aria-hidden className="mt-0.5 size-4 shrink-0" />
                  <div>
                    <strong>Kept inside this project.</strong>
                    <p className="text-muted-foreground">
                      Memory is scoped to this project. Source references are identifiers, never
                      secret values.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </aside>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PageHeading({ headingLevel = "h1" }: { headingLevel?: "h1" | "h2" } = {}) {
  return <PageHeader title="Memory" headingLevel={headingLevel} />;
}
